import React from 'react';

import { getCurrentContent, getCurrentContentId } from '@lumapps/contents/ducks/selectors';
import type { WriteModeContent } from '@lumapps/contents/types';
import {
    ContentEditorContextualActions,
    type ContentEditorContextualActionsProps,
} from '@lumapps/designer-content-editor/components/ContentEditorContextualActions';
import {
    ContentEditorTopbar,
    type ContentEditorTopbarProps,
} from '@lumapps/designer-content-editor/components/ContentEditorTopbar';
import type { ContentEditorActions } from '@lumapps/designer-content-editor/context/ContentEditorContext';
import {
    LegacyContentEditorProvider,
    type LegacyContentEditorProviderProps,
} from '@lumapps/designer-content-editor/context/LegacyContentEditorProvider';
import type { ContentSave, ContentUpdate } from '@lumapps/designer-content-editor/types';
import { useLiveContentRevision } from '@lumapps/designer/hooks/useLiveContentRevision';
import type { LegacyContentSettingsDialogVariant } from '@lumapps/designer/types';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { useSelector } from '@lumapps/redux/react';

interface LegacyContentEditorControlsProps {
    hasChanges: ContentEditorTopbarProps['hasChanges'];
    setContributionMode: ContentEditorActions['updateContributionMode'];
    updateLegacyContent: ContentUpdate;
    onSettingsOpen: (variant?: LegacyContentSettingsDialogVariant) => void;
    onInputLanguageChange: LegacyContentEditorProviderProps['onInputLanguageChange'];
    saveContent: ContentSave;
    publishContent: LegacyContentEditorProviderProps['publishContent'];
    enableVersioningMode: ContentEditorContextualActionsProps['enableVersioningMode'];
    isVersionningPanelOpen?: boolean;
}

export const LegacyContentEditorControls: React.FC<LegacyContentEditorControlsProps> = ({
    setContributionMode,
    updateLegacyContent,
    onSettingsOpen,
    onInputLanguageChange,
    saveContent,
    publishContent,
    hasChanges,
    enableVersioningMode,
    isVersionningPanelOpen,
}) => {
    const contentId = useSelector(getCurrentContentId);
    const currentContent = useSelector(getCurrentContent) as WriteModeContent;
    const instanceId = useSelector(instanceIdSelector);

    // We need to get the current live revision to be able to compute the publication state correctly.
    // The PAGE_EDIT call is not enough to get the correct state.
    const { isLoading: isLiveContentRevisionLoading } = useLiveContentRevision({ contentId, instanceId });

    return (
        <LegacyContentEditorProvider
            onInputLanguageChange={onInputLanguageChange}
            onSettingsOpen={onSettingsOpen}
            saveContent={saveContent}
            updateLegacyContent={updateLegacyContent}
            updateContributionMode={setContributionMode}
            publishContent={publishContent}
        >
            <ContentEditorTopbar
                hasChanges={hasChanges}
                isVersionningPanelOpen={isVersionningPanelOpen}
                isLegacy
                currentContent={currentContent}
                isLoading={isLiveContentRevisionLoading}
            />
            <ContentEditorContextualActions
                enableVersioningMode={enableVersioningMode}
                isVersionningPanelOpen={isVersionningPanelOpen}
                hasChanges={hasChanges}
                customContentTypeDetails={currentContent?.customContentTypeDetails}
            />
        </LegacyContentEditorProvider>
    );
};
